import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import {
	ASSIGNABLE_ORDERS_FOR_INVOICE_QUERY_KEY,
	INVOICE_QUERY_KEY,
	useAssignableOrdersForInvoice,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import AssignProcessedInvoiceToOrderForm
	from "~/modules/billing/components/InvoiceDetailsView/tempComponents/AssignProcessedInvoiceToOrderSidebar/AssignProcessedInvoiceToOrderForm";
import { useProjectsProjectPhases } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	invoiceData: Invoice
};

const AssignProcessedInvoiceToOrderSidebar = ({
	isOpen,
	onClose,
	invoiceData,
}: Props) => {

	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async () => {
		await queryClient.refetchQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
		await queryClient.refetchQueries({ queryKey: INVOICE_QUERY_KEY(invoiceData.id) });
		onClose();
	}, [invoiceData.id, onClose, queryClient]);

	const {
		data: allProjectPhasesData,
		isLoading: allProjectPhasesIsLoading,
	} = useProjectsProjectPhases(invoiceData.projectId || "");

	const {
		data: allAvailableOrdersData,
		isFetching: allAvailableOrdersIsLoading,
	} = useAssignableOrdersForInvoice(isOpen && invoiceData.isProjectInvoice ? invoiceData.id : "");

	const handleClose = useCallback(() => {
		queryClient.removeQueries({ queryKey: ASSIGNABLE_ORDERS_FOR_INVOICE_QUERY_KEY(invoiceData.id) });
		onClose();
	}, [invoiceData.id, onClose, queryClient]);

	const isLoading = allProjectPhasesIsLoading || allAvailableOrdersIsLoading;
	const showForm = isOpen && !isLoading;

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={handleClose}>
			{isLoading && <SidebarBusyOverlay />}
			{showForm && <AssignProcessedInvoiceToOrderForm
				key={showForm ? "form-open" : "form-closed"}
				onCancel={handleClose}
				onSuccess={handleSuccess}
				allPhasesData={allProjectPhasesData || []}
				allAvailableOrders={allAvailableOrdersData || []}
				invoiceData={invoiceData} />}
		</Sidebar>
	);
};

export default AssignProcessedInvoiceToOrderSidebar;
